import { BreakpointObserver } from "@angular/cdk/layout";
import { ViewportScroller } from "@angular/common";
import { AfterViewInit, Component, OnDestroy, OnInit, Renderer2, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { fromEvent, Subscription } from "rxjs";
import LocalizationConstants from "src/app/constants/localization-constants";
import RouteNameConstants from "src/app/constants/route-name-constants";
import { LoginStatus } from "src/app/models/Enums";
import { BrandingService } from "src/app/services/branding.service";
import { CompanyService } from "src/app/services/company.service";
import { TechCoverVisibilityService } from "src/app/services/tech-cover-visibility.service";
import { ToolFilterService } from "src/app/services/tool-filter.service";

import { ModalConfig, ModalService } from "../../services/modal.service";
import { SearchModalComponent, SearchModalData } from "../search-modal/search-modal.component";
import { AuthService } from "./../../services/auth.service";
import { PoBasketService } from "./../../services/po-basket.service";
import { RaBasketService } from "./../../services/ra-basket.service";
import { UserSettingsComponent } from "./../user-settings/user-settings.component";

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"],
  standalone: false,
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("searchInput") searchInput;
  @ViewChild("searchInputToggle") searchInputToggle;
  private readonly _subscriptions$: Subscription[] = [];
  readonly routeNameConstants = RouteNameConstants;
  localizationConstants = LocalizationConstants;
  showUserMenu: boolean;
  isSearchInputVisible: boolean = true; // by default is visible
  isSearchInputExpanded = false;
  quickSearch: string;
  page: number;
  yPosition: number;
  private clickUnlisten: () => void;
  private userSettingsModalOpen: boolean;

  public isMobileScreen: boolean = true;
  public isTechCoverEnabled = this.techCoverVisibilityService.isTechCoverComputed;

  constructor(
    public authService: AuthService,
    private router: Router,
    public raBasket: RaBasketService,
    public poBasket: PoBasketService,
    public companyService: CompanyService,
    private modalService: ModalService,
    private renderer: Renderer2,
    private viewPort: ViewportScroller,
    public brandingService: BrandingService,
    private breakpointObserver: BreakpointObserver,
    private techCoverVisibilityService: TechCoverVisibilityService,
    private toolFilterService: ToolFilterService,
  ) {}

  ngOnInit() {
    this._subscriptions$.push(this.onViewPortChange());
    this._subscriptions$.push(this.onScroll());
    this._subscriptions$.push(this.onLoginChange());
    this.clickUnlisten = this.onClickAnywhere();
  }

  ngAfterViewInit() {
    if (this.authService.isLoggedIn && !this.companyService.selectedCompany) {
      this.openMenu();
    }
  }

  onLoginChange() {
    return this.authService.isUserProfileSet$.subscribe((userProfileSet: LoginStatus) => {
      if (userProfileSet !== LoginStatus.LoggedIn) {
        return;
      }

      if (!this.companyService.selectedCompany) {
        this.openMenu();
      }
    });
  }

  private onScroll() {
    return fromEvent(window, "scroll").subscribe((e: Event) => this.setYPosition(e));
  }

  private onViewPortChange(): Subscription {
    return (
      this.breakpointObserver
        // Approximate min width of the screen where full size logo and all icons can fit without overflow
        .observe(["(max-width: 500px)"])
        .subscribe((result) => {
          this.isMobileScreen = result.matches;
        })
    );
  }

  private setYPosition(e: Event) {
    const yPos = (e.target as Element).children[0].scrollTop;
    // Snap to top when scrolling up to avoid header jumping
    if (yPos < this.yPosition && yPos < 20) {
      this.viewPort.scrollToPosition([0, 0]);
    }
    this.yPosition = yPos;
  }

  onSearchSubmit() {
    if (!this.quickSearch) return;

    this.showSearchModal();

    this.quickSearch = "";
  }

  showSearchModal() {
    const filter = this.toolFilterService.create();
    filter.search = this.quickSearch;

    const modalConfig = new ModalConfig<SearchModalData>();
    modalConfig.data = {
      company: this.companyService.selectedCompany,
      quickSearch: this.quickSearch,
      filter: filter,
    };
    modalConfig.panelClass = "tool-search__modal";

    this.modalService.openModal(SearchModalComponent, modalConfig);
  }

  isLoggedIn() {
    return this.authService.isLoggedIn;
  }

  showLogin() {
    return !this.isLoggedIn() && !this.router.url.startsWith(RouteNameConstants.LOGIN);
  }

  openMenu() {
    // if modal is open, no need to open it second time
    // multiple menu open happens on region change, when we already have a opened menu
    // and changing the loggedIn user
    if (this.userSettingsModalOpen) {
      return;
    }
    const modalConfig = new ModalConfig();
    modalConfig.width = "375px";
    modalConfig.minHeight = "150px";
    modalConfig.panelClass = "user-menu";
    modalConfig.position = { top: "60px", right: "49px" };
    // mark the modal is closed
    const subscription = this.modalService
      .openModal(UserSettingsComponent, modalConfig)
      .subscribe(() => (this.userSettingsModalOpen = false));

    this._subscriptions$.push(subscription);

    // mark that modal is open
    this.userSettingsModalOpen = true;
  }

  onSearchButtonClick() {
    this.isSearchInputExpanded = !this.isSearchInputExpanded;
    if (this.isSearchInputExpanded) {
      this.searchInput.nativeElement.focus();
    }
  }

  private onClickAnywhere() {
    return this.renderer.listen("window", "click", (e: Event) => {
      if (
        e.target !== this.searchInputToggle?.nativeElement &&
        e.target !== this.searchInput?.nativeElement
      ) {
        this.isSearchInputExpanded = false;
      }
    });
  }

  ngOnDestroy() {
    this._subscriptions$.forEach((a) => a.unsubscribe());
    this.clickUnlisten();
  }
}
